/** Shapes shared by the sales server and web halves. Pure types: no DOM, no Worker APIs. */
import type { JobDetail, Money, Msg, Ref, SubjectRef } from "../../../contract";

export type OfferingKind = "production_sale" | "private_treaty" | "online_auction" | "sale_barn";
export type LotKind = "animal" | "group" | "pair" | "semen" | "embryos" | "interest" | "other";
export type LotStage = "draft" | "ready" | "published";
export type LotClosed = "withdrawn" | "no_sale";
export type LotStatus = "draft" | "ready" | "published" | "inquiring" | "reserved" | "sold" | "no_sale" | "withdrawn" | "delivered";
export type PriceBasis = "total" | "per_head" | "per_lot" | "per_cwt" | "per_lb";

export const LOT_STATUS_LABELS: Record<LotStatus, string> = {
  draft: "Draft", ready: "Ready", published: "Published", inquiring: "Buyer interest", reserved: "Reserved", sold: "Sold",
  no_sale: "No sale", withdrawn: "Withdrawn", delivered: "Delivered",
};
export const OFFERING_KIND_LABELS: Record<OfferingKind, string> = {
  production_sale: "Production sale", private_treaty: "Private treaty", online_auction: "Online or video auction", sale_barn: "Sale barn consignment",
};
export const LOT_KIND_LABELS: Record<LotKind, string> = {
  animal: "Animals", group: "Counted head", pair: "Pair", semen: "Semen", embryos: "Embryos", interest: "Retained interest", other: "Other",
};
export const PRICE_BASIS_LABELS: Record<PriceBasis, string> = {
  total: "Total", per_head: "Per head", per_lot: "Per lot", per_cwt: "Per hundredweight", per_lb: "Per pound",
};

/** Offering settings kept in jobs.data for a sales.offering job. The sale date is jobs.due_at. */
export interface OfferingData {
  kind?: OfferingKind;
  location?: string;
  manager_id?: string;
  terms?: string;
  price_basis?: PriceBasis;
  auction_mode?: "timed" | "live";
  opens_at?: string;                 // ranch-local "YYYY-MM-DDTHH:mm"
  closes_at?: string;                // ranch-local "YYYY-MM-DDTHH:mm"
  increment?: Money;
  extension_window_minutes?: number;
  extension_minutes?: number;
  notes?: string;
}

/** A sales event in the shape the pure helpers need. */
export interface SaleEventLite {
  id: string;
  root_id: string;
  type: string;
  occurred_at: string | null;
  recorded_at: string;
  data: Record<string, any>;
  subjects: SubjectRef[];
}

export interface LotLike {
  id: string;
  lot_number: string;
  former_numbers: string[];
  head_count: number | null;
  stage: LotStage;
  closed: LotClosed | null;
}

export interface LotState {
  lot_id: string;
  status: LotStatus;
  members: string[];
  inquiries: Array<{ event_id: string; buyer_id: string | null; on: string | null; follow_up_on: string | null; open: boolean }>;
  offers: Array<{ event_id: string; buyer_id: string; on: string | null; amount: Money | null; price_basis: PriceBasis | null; expires: string | null; open: boolean }>;
  reservation: { event_id: string; buyer_id: string; on: string | null; until: string | null; deposit: Money | null } | null;
  agreement: { event_id: string; root_id: string; buyer_id: string; on: string | null; price: Money | null; price_basis: PriceBasis | null; delivery_by: string | null; deposit: Money | null; channel: string | null; retained: string | null } | null;
  delivered: string[];
  delivered_by_lot: boolean;
  delivery_events: string[];
  transfers: Record<string, string>;
}

export type CheckStatus = "ok" | "warn" | "block" | "unknown";
export interface ReadinessCheck {
  key: string;
  status: CheckStatus;
  label: Msg;
  animal_id?: string;
  link?: string;
}
export interface ReadinessSummary { ok: number; warn: number; block: number; unknown: number }

export interface LotAnimal { id: string; label: string; tag: string | null; name: string | null; sex: string; class: string; birth_date: string | null; status: string; photos: number }

export interface LotDTO {
  id: string;
  offering_id: string;
  lot_number: string;
  position: number;
  kind: LotKind;
  head_count: number | null;
  title: string | null;
  footnote: string | null;
  retained: string | null;
  asking: Money | null;
  /** Present only for people who manage the sale. */
  reserve?: Money | null;
  price_basis: PriceBasis | null;
  stage: LotStage;
  closed: LotClosed | null;
  status: LotStatus;
  animals: LotAnimal[];
  buyer: Ref | null;
  reservation: (LotState["reservation"] & { buyer: Ref }) | null;
  agreement: (LotState["agreement"] & { buyer: Ref; invoice?: InvoiceLink | null }) | null;
  inquiries: number;
  offers: number;
  delivered: number;
  readiness: ReadinessSummary;
  checks: ReadinessCheck[];
  closes_at: string | null;
  /** Registration transfer: the least finished status across the lot's animals, when any was recorded. */
  transfer: { status: string; done: number; total: number } | null;
}

export interface InvoiceLink { id: string; status: string; total: Money; number: string | null }

export interface BuyerActivity {
  event_id: string;
  type: "sales.inquiry" | "sales.offer" | "sales.reservation" | "sales.agreement";
  buyer: Ref;
  offering: Ref;
  on: string | null;
  lots: string[];
  subjects: SubjectRef[];
  summary: string | null;
  follow_up_on: string | null;
  follow_up: string | null;
  task_id: string | null;
  open: boolean;
  data: Record<string, unknown>;
}

export interface DeliveryDTO {
  event_id: string | null;
  lot_id: string | null;
  lot_number: string | null;
  buyer: Ref | null;
  due: string | null;
  delivered_on: string | null;
  animals: Ref[];
  remaining: number;
  offering: Ref;
}

export interface CatalogSummary {
  id: string;
  version: number;
  status: "proof" | "approved" | "superseded";
  created_at: string;
  approved_at: string | null;
  approved_by: string | null;
  shared: boolean;
  share_token: string | null;
  changes: CatalogChange[];
  note: string | null;
}

export interface OfferingDTO {
  job: JobDetail;
  data: OfferingData;
  kind: OfferingKind;
  sale_date: string | null;
  lots: LotDTO[];
  unassigned: Array<{ id: string; label: string }>;
  counts: Partial<Record<LotStatus, number>>;
  readiness: ReadinessSummary & { lots_with_issues: number };
  buyers: BuyerActivity[];
  deliveries: DeliveryDTO[];
  catalogs: CatalogSummary[];
  bidders: BidderDTO[];
  auction: { mode: "timed" | "live"; opens_at: string | null; closes_at: string | null; locked: boolean; increment: Money | null } | null;
  can: { manage: boolean; finance_read: boolean; finance_write: boolean };
}

export interface OfferingCard {
  id: string;
  title: string;
  kind: OfferingKind;
  status: string;
  sale_date: string | null;
  lots: number;
  counts: Partial<Record<LotStatus, number>>;
  readiness: ReadinessSummary & { lots_with_issues: number };
  catalog: { version: number; status: string } | null;
}

export interface SalesOverview {
  offerings: OfferingCard[];
  past: OfferingCard[];
  follow_ups: BuyerActivity[];
  offers: BuyerActivity[];
  holds: BuyerActivity[];
  agreements: BuyerActivity[];
  deliveries: DeliveryDTO[];
  purchases: PurchaseCard[];
  can: { manage: boolean };
}

// ---------------------------------------------------------------- catalog snapshots

export interface CatalogWeight { value: number; unit: string; on: string | null; adjusted?: number | null }
export interface CatalogAnimal {
  key: string;
  tag: string | null;
  name: string | null;
  sex: string;
  birth_date: string | null;
  breed: string | null;
  color: string | null;
  registration: { association: string | null; number: string } | null;
  pedigree: Partial<Record<"sire" | "dam" | "sire_sire" | "sire_dam" | "dam_sire" | "dam_dam", string>>;
  epds: { as_of: string | null; values: Record<string, number> } | null;
  weights: Partial<Record<"birth" | "weaning" | "yearling" | "last", CatalogWeight>>;
  adg: number | null;
  photos: Array<{ id: string; name: string | null }>;
}
export interface CatalogLot {
  lot_id: string;
  lot_number: string;
  kind: LotKind;
  head_count: number | null;
  title: string | null;
  footnote: string | null;
  retained: string | null;
  asking: Money | null;
  price_basis: PriceBasis | null;
  animals: CatalogAnimal[];
}
export interface CatalogSnapshot {
  offering: { id: string; title: string; kind: OfferingKind; sale_date: string | null; location: string | null; terms: string | null; ranch: string; manager: string | null };
  lots: CatalogLot[];
  built_at: string;
}
export interface CatalogChange {
  lot: string;
  animal?: string;
  field: string;
  change: "added" | "removed" | "changed";
  before: string | number | null;
  after: string | number | null;
}
export interface CatalogVersionDTO extends CatalogSummary {
  offering: Ref;
  snapshot: CatalogSnapshot;
}
export interface PublicCatalogDTO {
  version: number;
  approved_at: string | null;
  changes: CatalogChange[];
  snapshot: CatalogSnapshot;
}

// ---------------------------------------------------------------- auctions

export interface BidderDTO { id: string; contact: Ref; number: string; status: "approved" | "pending" | "suspended" }
export interface BidDTO {
  id: string;
  sequence: number;
  bidder: { id: string; number: string; label: string };
  amount: Money;
  /** True when the bidder left a private maximum; the amount itself is never shown. */
  has_max: boolean;
  channel: string;
  received_at: string;
  status: "received" | "accepted" | "rejected" | "retracted";
  reason: string | null;
  retract_reason: string | null;
}
export interface BidReceipt {
  bid_id: string;
  status: "accepted" | "rejected";
  reason: string | null;
  price: Money | null;
  leading: boolean;
  closes_at: string | null;
  duplicate: boolean;
}
export interface AuctionLotState {
  lot_id: string;
  price: Money | null;
  leader: { bidder_id: string; number: string; label: string } | null;
  closes_at: string | null;
  extended: number;
  open: boolean;
  reserve_met: boolean | null;
  bids: BidDTO[];
}

// ---------------------------------------------------------------- buyer packets

export type PacketCategory = "identity" | "pedigree" | "health" | "performance" | "genetics" | "breeding" | "sale";
export const PACKET_CATEGORY_LABELS: Record<PacketCategory, string> = {
  identity: "Identity and tags", pedigree: "Pedigree", health: "Health and vaccinations", performance: "Weights and measurements",
  genetics: "Registration, DNA and EPDs", breeding: "Breeding and calving", sale: "Sale terms and retained interests",
};

export interface PacketRecord {
  key: string;
  revision: number;
  type: string;
  category: PacketCategory;
  label: string;
  role: string;
  occurred_at: string | null;
  precision: string;
  summary: string;
  data: Record<string, unknown>;
}
export interface PacketAnimal {
  key: string;
  tag: string | null;
  name: string | null;
  sex: string;
  castrated: boolean;
  birth_date: string | null;
  birth_precision: string | null;
  breed: string | null;
  color: string | null;
  identifiers: Array<{ scheme: string; value: string; issuer: string | null }>;
  pedigree: Partial<Record<"sire" | "dam" | "sire_sire" | "sire_dam" | "dam_sire" | "dam_dam", { label: string; registration: string | null }>>;
  sale: { offering: string | null; lot: string | null; delivered_on: string | null; guarantee: string | null; retained: string | null; conditions: string | null } | null;
  records: PacketRecord[];
}
export interface PacketSnapshot {
  version: number;
  from_ranch: string;
  recipient: string | null;
  purpose: string | null;
  language: "en" | "es";
  categories: PacketCategory[];
  created_at: string;
  animals: PacketAnimal[];
}
export interface PacketChange { animal: string; record?: string; label: string; change: "added" | "removed" | "changed" }
export interface PacketDTO {
  id: string;
  root_id: string;
  version: number;
  token: string;
  status: "shared" | "superseded" | "revoked";
  recipient: Ref | null;
  purpose: string | null;
  language: "en" | "es";
  categories: PacketCategory[];
  created_at: string;
  revoked_at: string | null;
  changes: PacketChange[];
  snapshot: PacketSnapshot;
  versions: Array<{ id: string; version: number; created_at: string; status: string }>;
}

export interface ReceiveCandidate { id: string; label: string; reason: "eid" | "registration" | "tag" | "imported"; strong: boolean }
export interface ReceivePreview {
  token: string;
  packet_id: string;
  version: number;
  from_ranch: string;
  own_ranch: boolean;
  created_at: string;
  categories: PacketCategory[];
  purpose: string | null;
  prior: { version: number; imported_at: string } | null;
  changes: PacketChange[];
  animals: Array<{
    key: string;
    label: string;
    tag: string | null;
    name: string | null;
    sex: string;
    birth_date: string | null;
    breed: string | null;
    identifiers: Array<{ scheme: string; value: string }>;
    records: number;
    candidates: ReceiveCandidate[];
    suggested: string;
    imported_as: Ref | null;
  }>;
  snapshot: PacketSnapshot;
}
export interface ReceiveResult {
  animals: Array<{ key: string; id: string; label: string; created: boolean }>;
  records: { added: number; changed: number; removed: number };
  version: number;
}

// ---------------------------------------------------------------- purchases

export type PurchaseKind = "cattle" | "semen" | "feed" | "health" | "service" | "other";
export interface PurchaseCard {
  id: string;
  title: string;
  kind: PurchaseKind | null;
  status: string;
  stage: string | null;
  supplier: Ref | null;
  needed_by: string | null;
  quotes: number;
  ordered: { on: string | null; expected_on: string | null; amount: Money | null } | null;
  received: { on: string | null; condition: string | null } | null;
}

export interface AnimalSaleInfo {
  listing: { offering: Ref; lot_id: string | null; lot_number: string; status: LotStatus; listed_on: string | null } | null;
  offerings: Array<{ offering: Ref; lot_number: string | null; status: string }>;
  reservation: { buyer: Ref; until: string | null; on: string | null } | null;
  agreement: { event_id: string; buyer: Ref; on: string | null; price: Money | null; price_basis: string | null; delivery_by: string | null; retained: string | null; guarantee: string | null; invoice: InvoiceLink | null } | null;
  delivery: { event_id: string; on: string | null; buyer: Ref | null; trucker: Ref | null; health_papers: string | null; destination: string | null } | null;
  transfer: { status: string; on: string | null } | null;
  packets: Array<{ id: string; version: number; recipient: Ref | null; created_at: string; status: string }>;
  received: { from_ranch: string; version: number; on: string | null } | null;
  can: { manage: boolean; finance_read: boolean };
}
